import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  ArrowRight,
  Link2,
} from "lucide-react";

const ReverseLinkedListVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Five elements",
      list: [1, 2, 3, 4, 5],
      expected: [5, 4, 3, 2, 1],
    },
    {
      id: 2,
      name: "Two elements",
      list: [1, 2],
      expected: [2, 1],
    },
    {
      id: 3,
      name: "Single element",
      list: [1],
      expected: [1],
    },
    {
      id: 4,
      name: "Three elements",
      list: [10, 20, 30],
      expected: [30, 20, 10],
    },
    {
      id: 5,
      name: "Four elements",
      list: [7, 14, 21, 28],
      expected: [28, 21, 14, 7],
    },
    {
      id: 6,
      name: "Ascending order",
      list: [1, 3, 5, 7, 9],
      expected: [9, 7, 5, 3, 1],
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [originalList, setOriginalList] = useState(testCases[0].list);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Algorithm approach
  const [approach, setApproach] = useState("iterative"); // "iterative" or "recursive"

  // Pseudo code for both approaches
  const iterativePseudoCode = [
    { line: "function reverseList(head):", indent: 0 },
    { line: "prev = null", indent: 1 },
    { line: "current = head", indent: 1 },
    { line: "while (current != null):", indent: 1 },
    { line: "next = current.next  // Save next", indent: 2 },
    { line: "current.next = prev  // Reverse pointer", indent: 2 },
    { line: "prev = current       // Move prev", indent: 2 },
    { line: "current = next       // Move current", indent: 2 },
    { line: "return prev  // New head", indent: 1 },
  ];

  const recursivePseudoCode = [
    { line: "function reverseList(head):", indent: 0 },
    { line: "// Base case", indent: 1 },
    { line: "if (!head || !head.next):", indent: 1 },
    { line: "return head", indent: 2 },
    { line: "// Recursive case", indent: 1 },
    { line: "newHead = reverseList(head.next)", indent: 1 },
    { line: "head.next.next = head  // Reverse link", indent: 1 },
    { line: "head.next = null       // Break old link", indent: 1 },
    { line: "return newHead", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Node class for linked list representation
  class Node {
    constructor(value, index) {
      this.value = value;
      this.index = index;
      this.next = null;
    }
  }

  // Create linked list from array
  const createLinkedList = (arr) => {
    if (arr.length === 0) return null;

    const head = new Node(arr[0], 0);
    let current = head;

    for (let i = 1; i < arr.length; i++) {
      current.next = new Node(arr[i], i);
      current = current.next;
    }

    return head;
  };

  // Calculate all steps for iterative approach
  const calculateIterativeSteps = (list) => {
    const allSteps = [];
    const linkedList = createLinkedList(list);

    // Initial state
    allSteps.push({
      iteration: 0,
      list: listToArray(linkedList),
      prev: null,
      current: linkedList ? linkedList.value : null,
      next: null,
      currentIndex: linkedList ? 0 : -1,
      prevIndex: -1,
      nextIndex: linkedList && linkedList.next ? 1 : -1,
      arrows: generateArrows(linkedList),
      description: "Initial state: Set prev = null, current = head",
      highlightLine: 1,
    });

    let prev = null;
    let current = linkedList;
    let iteration = 0;

    while (current !== null) {
      iteration++;
      const next = current.next;
      const currentValue = current.value;
      const currentIdx = current.index;

      // Step 1: Save next
      allSteps.push({
        iteration,
        list: listToArray(linkedList),
        prev: prev ? prev.value : null,
        current: currentValue,
        next: next ? next.value : null,
        currentIndex: currentIdx,
        prevIndex: prev ? prev.index : -1,
        nextIndex: next ? next.index : -1,
        arrows: generateArrows(linkedList),
        description: `Save next pointer: next = ${next ? next.value : "null"}`,
        highlightLine: 4,
      });

      // Step 2: Reverse the pointer
      current.next = prev;

      allSteps.push({
        iteration,
        list: listToArray(linkedList),
        prev: prev ? prev.value : null,
        current: currentValue,
        next: next ? next.value : null,
        currentIndex: currentIdx,
        prevIndex: prev ? prev.index : -1,
        nextIndex: next ? next.index : -1,
        arrows: generateArrowsWithReverse(
          linkedList,
          currentIdx,
          prev ? prev.index : -1
        ),
        reversedArrow: { from: currentIdx, to: prev ? prev.index : -1 },
        description: `Reverse pointer: ${currentValue} → ${
          prev ? prev.value : "null"
        }`,
        highlightLine: 5,
      });

      // Step 3: Move pointers
      prev = current;
      current = next;

      allSteps.push({
        iteration,
        list: listToArray(linkedList),
        prev: prev.value,
        current: current ? current.value : null,
        next: null,
        currentIndex: current ? current.index : -1,
        prevIndex: prev.index,
        nextIndex: -1,
        arrows: generateArrowsFromPrev(prev),
        description: `Move pointers: prev = ${prev.value}, current = ${
          current ? current.value : "null"
        }`,
        highlightLine: 6,
      });
    }

    // Final state
    allSteps.push({
      iteration: iteration + 1,
      list: listToArray(prev),
      prev: prev ? prev.value : null,
      current: null,
      next: null,
      currentIndex: -1,
      prevIndex: prev ? prev.index : -1,
      nextIndex: -1,
      arrows: generateArrowsFromPrev(prev),
      description: "Complete! Return prev as new head",
      highlightLine: 8,
      isFinal: true,
    });

    return allSteps;
  };

  // Helper functions
  const listToArray = (head) => {
    const result = [];
    let current = head;
    while (current) {
      result.push({ value: current.value, index: current.index });
      current = current.next;
    }
    return result;
  };

  const generateArrows = (head) => {
    const arrows = [];
    let current = head;
    while (current && current.next) {
      arrows.push({ from: current.index, to: current.next.index });
      current = current.next;
    }
    return arrows;
  };

  const generateArrowsWithReverse = (head, fromIdx, toIdx) => {
    const arrows = generateArrows(head);
    return arrows.filter((arrow) => !(arrow.from === fromIdx));
  };

  const generateArrowsFromPrev = (prev) => {
    return generateArrows(prev);
  };

  // Initialize steps when test case changes
  useEffect(() => {
    const newSteps =
      approach === "iterative"
        ? calculateIterativeSteps(originalList)
        : calculateIterativeSteps(originalList); // For now, using iterative for both
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [originalList, approach]);

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying || finished) return;

    const timer = setTimeout(() => {
      handleNextStep();
    }, speed);

    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      const step = steps[nextStep];
      setHighlightedLine(step.highlightLine);

      if (step.isFinal) {
        setFinished(true);
        setIsPlaying(false);
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      const step = steps[prevStep];
      setHighlightedLine(step.highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setOriginalList(testCases[index].list);
  };

  const addCustomTestCase = () => {
    try {
      const cleanInput = customInput.trim().replace(/[\[\]]/g, "");
      const numbers = cleanInput.split(",").map((n) => parseInt(n.trim()));

      if (numbers.some(isNaN) || numbers.length === 0) {
        alert("Please enter valid numbers");
        return;
      }

      const newTestCase = {
        id: Date.now(),
        name: `Custom ${testCases.length - 5}`,
        list: numbers,
        expected: [...numbers].reverse(),
      };

      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
    } catch (error) {
      alert("Invalid input format");
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      const newTestCases = testCases.filter((tc) => tc.id !== id);
      setTestCases(newTestCases);
      if (selectedTestCase >= newTestCases.length) {
        handleTestCaseChange(0);
      }
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Link2 className="text-blue-500" size={24} />
        Reverse Linked List
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left side - Visualization */}
        <div className="space-y-3">
          {/* Approach Selector */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Select Approach</h3>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  setApproach("iterative");
                  reset();
                }}
                className={`px-3 py-1.5 text-xs rounded transition-colors ${
                  approach === "iterative"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
              >
                Iterative - O(n)
              </button>
              <button
                onClick={() => {
                  setApproach("recursive");
                  reset();
                }}
                className={`px-3 py-1.5 text-xs rounded transition-colors ${
                  approach === "recursive"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
              >
                Recursive - O(n)
              </button>
            </div>
          </div>

          {/* Test Cases */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-36 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      [{tc.list.join(", ")}] → [{tc.expected.join(", ")}]
                    </div>
                  </button>
                  {tc.id > 6 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add custom test case */}
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="List: 1,2,3,4,5"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} />
                Add Test Case
              </button>
            </div>
          </div>

          {/* Linked List Visualization */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            {/* Pointers Display */}
            <div className="mb-3 p-2 bg-gray-50 rounded">
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div
                  className={`p-1 rounded ${
                    currentStepData?.prevIndex >= 0 ? "bg-orange-100" : ""
                  }`}
                >
                  <span className="font-medium text-orange-700">prev: </span>
                  <span className="font-mono">
                    {currentStepData?.prev ?? "null"}
                  </span>
                </div>
                <div
                  className={`p-1 rounded ${
                    currentStepData?.currentIndex >= 0 ? "bg-purple-100" : ""
                  }`}
                >
                  <span className="font-medium text-purple-700">current: </span>
                  <span className="font-mono">
                    {currentStepData?.current ?? "null"}
                  </span>
                </div>
                <div
                  className={`p-1 rounded ${
                    currentStepData?.nextIndex >= 0 ? "bg-green-100" : ""
                  }`}
                >
                  <span className="font-medium text-green-700">next: </span>
                  <span className="font-mono">
                    {currentStepData?.next ?? "null"}
                  </span>
                </div>
              </div>
            </div>

            {/* Linked List Visual */}
            <div className="overflow-x-auto py-4">
              <div className="flex items-center gap-2 min-w-max">
                {currentStepData?.list.map((node, idx) => (
                  <div key={`node-${node.index}`} className="relative">
                    <div
                      className={`w-12 h-12 flex items-center justify-center border-2 rounded transition-all duration-300 ${
                        node.index === currentStepData.prevIndex
                          ? "bg-orange-100 border-orange-500 scale-110"
                          : node.index === currentStepData.currentIndex
                          ? "bg-purple-100 border-purple-500 scale-110"
                          : node.index === currentStepData.nextIndex
                          ? "bg-green-100 border-green-500 scale-110"
                          : "bg-gray-100 border-gray-300"
                      }`}
                    >
                      <span className="font-mono font-bold text-sm">
                        {node.value}
                      </span>
                    </div>
                    <div className="text-xs text-center mt-1 text-gray-500">
                      [{node.index}]
                    </div>

                    {/* Arrow to next */}
                    {idx < currentStepData.list.length - 1 && (
                      <div className="absolute top-1/2 -right-2 transform -translate-y-1/2">
                        <ArrowRight size={16} className="text-gray-400" />
                      </div>
                    )}
                  </div>
                ))}

                {/* Show null at the end */}
                {currentStepData?.list.length > 0 && (
                  <div className="ml-2 px-2 py-1 bg-gray-200 rounded text-xs text-gray-600">
                    null
                  </div>
                )}
              </div>

              {/* Show reversed arrow */}
              {currentStepData?.reversedArrow && (
                <div className="mt-4 p-2 bg-blue-50 rounded">
                  <p className="text-xs text-blue-800">
                    New pointer:{" "}
                    {
                      currentStepData.list[currentStepData.reversedArrow.from]
                        ?.value
                    }{" "}
                    →{" "}
                    {currentStepData.reversedArrow.to >= 0
                      ? currentStepData.list[currentStepData.reversedArrow.to]
                          ?.value
                      : "null"}
                  </p>
                </div>
              )}
            </div>

            {/* Result */}
            {currentStepData?.isFinal && (
              <div className="mt-3 p-3 bg-green-100 rounded">
                <h4 className="text-sm font-bold text-green-900">
                  List Reversed Successfully!
                </h4>
                <p className="text-xs text-green-700 mt-1">
                  Final order: [
                  {currentStepData.list.map((n) => n.value).join(" → ")}]
                </p>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep === 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>

              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                {isPlaying ? "Pause" : "Play"}
              </button>

              <button
                onClick={handleNextStep}
                disabled={currentStep === steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>

              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} />
                Reset
              </button>

              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="2000"
                  step="200"
                  value={2200 - speed}
                  onChange={(e) => setSpeed(2200 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>

            {/* Step indicator */}
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right side - Pseudo Code & Analysis */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">
              {approach === "iterative"
                ? "Iterative Algorithm"
                : "Recursive Algorithm"}
            </h3>
            <div className="space-y-0.5">
              {(approach === "iterative"
                ? iterativePseudoCode
                : recursivePseudoCode
              ).map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>

          {/* Complexity Analysis */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Complexity Analysis</h3>
            <div className="grid grid-cols-2 gap-3">
              <div
                className={`p-2 rounded ${
                  approach === "iterative" ? "bg-blue-50" : "bg-gray-50"
                }`}
              >
                <h4 className="text-xs font-medium text-gray-700 mb-1">
                  Iterative
                </h4>
                <div className="space-y-1 text-xs">
                  <div>
                    <span className="font-medium">Time:</span> O(n)
                  </div>
                  <div>
                    <span className="font-medium">Space:</span> O(1)
                  </div>
                </div>
              </div>
              <div
                className={`p-2 rounded ${
                  approach === "recursive" ? "bg-blue-50" : "bg-gray-50"
                }`}
              >
                <h4 className="text-xs font-medium text-gray-700 mb-1">
                  Recursive
                </h4>
                <div className="space-y-1 text-xs">
                  <div>
                    <span className="font-medium">Time:</span> O(n)
                  </div>
                  <div>
                    <span className="font-medium">Space:</span> O(n)
                  </div>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              Recursive uses O(n) space due to call stack
            </p>
          </div>

          {/* Algorithm Explanation */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">How It Works</h3>
            {approach === "iterative" ? (
              <ol className="space-y-1 text-xs text-gray-700">
                <li className="flex">
                  <span className="font-medium mr-1">1.</span>
                  <span>
                    Initialize three pointers: prev (null), current (head), next
                  </span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">2.</span>
                  <span>Save the next node before breaking the link</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">3.</span>
                  <span>
                    Reverse the current node's pointer to point to prev
                  </span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">4.</span>
                  <span>Move prev and current one step forward</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">5.</span>
                  <span>Repeat until current becomes null</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">6.</span>
                  <span>Return prev, which is now the new head</span>
                </li>
              </ol>
            ) : (
              <ol className="space-y-1 text-xs text-gray-700">
                <li className="flex">
                  <span className="font-medium mr-1">1.</span>
                  <span>
                    Base case: if head is null or has no next, return head
                  </span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">2.</span>
                  <span>Recursively reverse the rest of the list</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">3.</span>
                  <span>Set head.next.next to head (reverse the link)</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">4.</span>
                  <span>Set head.next to null to avoid cycles</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">5.</span>
                  <span>Return the new head from recursive calls</span>
                </li>
              </ol>
            )}
          </div>

          {/* Key Insights */}
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>Always save the next pointer before reversing</span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Three pointers are sufficient for iterative approach
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>Recursive approach is elegant but uses more space</span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>The original head becomes the tail after reversal</span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Edge cases: empty list and single-node list remain unchanged
                </span>
              </li>
            </ul>
          </div>

          {/* Visual Guide */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Pointer References</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-orange-100 border-2 border-orange-500 rounded flex items-center justify-center">
                  <span className="text-xs font-bold">P</span>
                </div>
                <span className="text-xs">Previous node (orange)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-100 border-2 border-purple-500 rounded flex items-center justify-center">
                  <span className="text-xs font-bold">C</span>
                </div>
                <span className="text-xs">Current node (purple)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-100 border-2 border-green-500 rounded flex items-center justify-center">
                  <span className="text-xs font-bold">N</span>
                </div>
                <span className="text-xs">Next node (green)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReverseLinkedListVisualizer;
